<template>
	<el-drawer
        size="40%"
        title="Karen Assistant (beta)"
		direction="rtl"
        custom-class="karen-chat-drawer"
        :visible.sync="isVisible"
		:before-close="handleClose"
    >
		<div class="chat-container" v-loading="loading">
            <div class="messages" ref="messagesContainer">
                <div class="message dummy"></div>
                <div class="message assistant intro">
                    Hi <span>{{ user.name }},</span>
                    <br>
                    <br>
                    <span>- I am your virtual assistant.</span>
                    <br>
                    <span>- I can try to answer basic questions about VenueCrew venues.</span>
                    <br>
                    <span>- I will also endeavor to suggest venue names based on your query.</span>
                    <br>
                    <span>- I may not have knowledge about venues that are not with us or onboarded with us in the last 48 hours.</span>
                    <br>
                    <br>
                    <span>Some useful tips to help me help you:</span>
                    <ul>
                        <li>Check spelling.</li>
                        <li>If you are after a specific location/city, specify it.</li>
                        <li>Be clear and specific about your query. For example, if you want contact information of a venue,
                            ask: "What is the phone number of <strong>venue name</strong>?" or
                            "What is the email address of <strong>venue name</strong>?",<br>instead of just asking: "phone number please?"
                            <br>
                            <br>
                            <strong>example-2</strong>: instead of asking "elmswood estate room information", ask "What is the standing type capacity of Elmswood Estate
                            for wedding event?"
                            <br>
                            <br>
                            <strong>example-3:</strong> instead of asking "what is the best beach side venue", ask "which beach side venue in byron
                            bay would you recommend"?
                        </li>
                        <li>If you come across any error that says the request cannot be processed, try clearing the chat using the 'Clear Chat" button'
                            and start a new conversation.
                        </li>
                    </ul>
                    <span>I am new and still learning and only an artificial agent. Please verify my answers if in any kind of doubt.</span>
                    <br>
                    <br>

                    <div>How can i help you today?</div>
                </div>
                <div class="message" :class="chatClasses(message)" v-for="message in conversation">
                    <div v-if="message.role === 'user'">
                        {{ message.content }}
                    </div>
                    <div v-else>
                        <strong>Assistant:</strong>
                        <div v-html="message.content"></div>
                    </div>
                </div>
            </div>
            <div class="text-left">
                <el-button type="primary" size="small"
                    :disabled="isNewConversation"
                    @click="clear"
                >Clear Chat</el-button>
            </div>
            <div id="user-input">
                <textarea v-model="userMessage"
                    :rows="4"
                    maxlength="500"
                    @keydown.enter="($event) => sendMessage($event)"
                ></textarea>
            </div>
			<div class="close-area">
				<div class="row">
					<div class="col text-right">
						<el-button size="small" type="info" @click="handleClose">Close</el-button>
					</div>
				</div>
			</div>
		</div>
	</el-drawer>
</template>

<script>
import { HTTP } from '@/services';
import { Button, Drawer } from 'element-ui';

export default {
    emits: ['update:visible'],
	components: {
		[Drawer.name]: Drawer,
		[Button.name]: Button,
	},
	props: {
        visible: {
			type: Boolean,
			default: false
		},
        leadEventId: {
            type: Number,
            required: true
        },
        karenVenueList: {
            type: Object,
            default: () => { return {
                eventCityVenues: [],
                otherVenues: []
            }}
        },
		clearChat: {
			type: Boolean,
			default: false
		}
	},
	emits: ['update:clearChat'],
	data() {
		return {
			userMessage: null,
            conversation: [],
            loading: false
		};
	},
    mounted() {
    },
	computed: {
        chatClasses() {
            return (message) => {
                return {
                    'user bg-info': message.role === 'user',
                    'assistant': message.role === 'assistant',
                };
            };
        },
        isNewConversation() {
            return this.conversation.length <= 1;
        },
        isVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit('update:visible', value);
            }
        },
        user () {
            return {
                name: this.$store.getters['auth/getUser']?.data.displayName,
                id: this.$store.getters['auth/getUser']?.data.id
            }
        },
        contextForChat() {
            let allVenues = this.karenVenueList.eventCityVenues.concat(this.karenVenueList.otherVenues).slice(0, 50); // limit to 50 venues for performance
            return allVenues.map((venue, index) => {
                return {
                    venue_id: venue[0].venue_id,
                    venue_name: venue[0].venue_name,
                    rank: index + 1 // array index starts from 0, so we add 1 to make it human-readable index
                };

            });
        }
	},
	watch: {
		clearChat(newValue) {
			if (newValue) {
				this.clear();
			}
		}
	},
	methods: {
        scrollToLatestMessage() {
            this.$nextTick(function() {
                this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
            });
        },
        clear() {
            this.conversation = [];
            this.$store.commit('recommendations/updateAiAgentSuggestedVenues', []);
			this.$emit('update:clearChat', false);
        },
        async sendMessage($event) {
            if ($event.shiftKey) {
                return;
            }

            this.loading = true;

            $event.preventDefault();
            const tmp = this.userMessage;
            this.userMessage = '';

            this.conversation.push({
                role: 'user',
                content: tmp
            });

            this.scrollToLatestMessage();
            let resp = await HTTP.post(`v1/karen/` + this.leadEventId + `/chat`, {
                message: tmp,
                userId: this.user.id,
                isNewConversation: this.isNewConversation,
                rankedVenueList: this.contextForChat.flat(1)
            });

            this.processResponse(resp);

            this.loading = false;

        },
        processResponse(resp) {
            if (resp?.message) {
                let content = resp.message;
                let suggestedVenues = [];
                if (resp?.venues.length) {
                    content += '<br>';
                    resp?.venues.forEach(venue => {
                        if (venue.venue_id) {
                            suggestedVenues.push(venue.venue_id);
                        }

                        if (venue.venue_name) {
                            content += `<br><strong>${venue.venue_name}</strong>`;
                        }

                        if (venue.reasoning) {
                            content += `<br>${venue.reasoning}`;
                        }

                        content += '<br>';
                    });
                }

                this.conversation.push({
                    role: 'assistant',
                    content: content
                });

                this.scrollToLatestMessage();
                if (suggestedVenues.length) {
                    this.$store.commit('recommendations/updateAiAgentSuggestedVenues', suggestedVenues);
                }
            } else if (resp?.error) {
            } else {
                // console.log('No message returned from server');
                this.conversation.push({
                    role: 'assistant',
                    content: 'Sorry, there seems to have been a problem to process your query. Please try again or contact the VenueCrew Dev team.'
                });
            }
        },
        addToConversation(message, userType) {
            this.conversation.push({
                role: userType,
                content: message
            });
        },
		handleClose() {
        	this.$emit('update:visible', false);
		}
	}
};
</script>


<style lang="scss">
.el-drawer__header {
	span[role="heading"] {
		color: #000000;
		font-weight: 600;
        outline: 0;
	}
}

.el-drawer__body {
    padding: 0px 1px 0px 1px;
    height: 100%;
}
.chat-container {
    height: 100%;
    padding: 0px 4px;
    padding-bottom: 80px;
    overflow-y: auto;
    font-family: 'Effra'; //, Franklin Gothic Medium', 'Arial Narrow', Arial, sans-serif;
    font-size: 16px;
    line-height: 24px;
    color: #fff;
    display: flex;
    flex-direction: column;
    justify-content: end;
    scroll-behavior: smooth;

    .messages {
        height: 600px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        row-gap: 5px;
        background-color: #333;
        padding: 5px;
        margin-bottom: 10px;
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;

        .message {
            margin-bottom: 10px;
            padding: 3px 5px;

            &:not(.intro) {
                max-width: 80%;
            }
            &.user {
                align-self: flex-end;
                text-align: right;
                border-radius: 5px;
                border-top-right-radius: 0;
            }

            &.assistant {
                align-self: flex-start;
                text-align: left;
            }

            &.intro {
                margin-bottom: 10px;
            }

            &.dummy {
                flex-grow: 1;
            }
        }
    }

    #user-input {
        margin-top: 5px;
        textarea {
            width: 100%;
            padding: 10px;
            border-radius: 5px;
            resize: none;
            font-size: 14px;
            outline: none;
            &:focus {
                // outline:blueviolet
                box-shadow: 0 0 5px rgba(81, 203, 238, 1);
                border: 1px solid rgba(81, 203, 238, 1);
            }
        }
    }
}

.close-area {
    height: 50px;
	position: fixed;
	bottom: 15px;
	right: 15px;
}
</style>
